// src/components/ServiceProviderSelectRenderer.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Stack,
  Box,
} from "@mui/material";
import axios from "axios";

const TAG_NOTIFY = "notify_audit_was_done";

function ServiceProviderSelectRenderer({
  module,
  companyId,
  responses,
  onChange,
  isEditable,
}) {
  const [providers, setProviders] = useState([]);
  const API_BASE_URL = "https://formbuilderapi.getforsite.com";

  useEffect(() => {
    const fetchProviders = async () => {
      if (!companyId) return;
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/service-providers`,
          { params: { company_id: companyId } },
        );
        const normalized = (data.providers || []).map((p) => ({
          ...p,
          id: String(p.id),
        }));
        setProviders(normalized);
      } catch (err) {
        console.error("Error fetching providers:", err);
        setProviders([]);
      }
    };
    fetchProviders();
  }, [companyId]);

  // ---- Normalize incoming saved value (supports legacy array and new object) ----
  const rawValue = responses?.[module.id];

  const selectionArray = useMemo(() => {
    if (Array.isArray(rawValue)) return rawValue.map(String);
    if (rawValue && Array.isArray(rawValue.selection)) {
      return rawValue.selection.map(String);
    }
    if (rawValue && Array.isArray(rawValue.selectedIds)) {
      // in case renderer previously saved { selectedIds: [...] }
      return rawValue.selectedIds.map(String);
    }
    return [];
  }, [rawValue]);

  const selectedId = selectionArray.length ? selectionArray[0] : "";

  const notifyChecked = useMemo(() => {
    if (Array.isArray(rawValue)) return false; // legacy had no tags
    const tags = (rawValue && (rawValue.tags || rawValue?.meta?.tags)) || [];
    return Array.isArray(tags) && tags.includes(TAG_NOTIFY);
  }, [rawValue]);

  // ---- Emit helper (keeps renderer's selectedIds but adds tags) ----
  const emit = (nextSelectionArr, notify) => {
    const payload = { selectedIds: nextSelectionArr };
    if (notify) payload.tags = [TAG_NOTIFY];
    onChange(payload);
  };

  const handleSelectionChange = (e) => {
    const next = e.target.value ? [String(e.target.value)] : [];
    emit(next, notifyChecked);
  };

  const handleNotifyToggle = (e) => {
    const nextNotify = !!e.target.checked;
    emit(selectionArray, nextNotify);
  };

  const labelText = module.label || "Select Service Provider";
  const labelId = `providers-label-${module.id}`;
  const selectId = `providers-select-${module.id}`;

  return (
    <Paper elevation={3} sx={{ p: 2, my: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">{labelText}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyChecked}
              onChange={handleNotifyToggle}
              disabled={true}
            />
          }
          label="notify audit was done"
          sx={{ ml: 2, whiteSpace: "nowrap" }}
        />
      </Stack>

      <Box mt={1}>
        <FormControl fullWidth variant="outlined" size="medium">
          <InputLabel id={labelId}>{labelText}</InputLabel>
          <Select
            labelId={labelId}
            id={selectId}
            label={labelText}
            value={selectedId}
            onChange={handleSelectionChange}
            disabled={!isEditable}
          >
            {providers.length > 0 ? (
              providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                {companyId ? "Loading providers..." : "Company ID not set"}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
}

export default ServiceProviderSelectRenderer;

/*
Notes:
- Adds a right-aligned checkbox that, when checked, includes tags: ["notify_audit_was_done"] in the onChange payload.
- Reads legacy array values and newer shapes: { selection:[...], tags:[...] } or { selectedIds:[...], tags:[...] }.
- Emits { selectedIds:[...], tags?:[...] } to preserve the renderer’s prior contract while adding tags.
*/
